/**
 * A ação que um componente dinâmico executa quando se clica nele.
 *
 * É o que a ferramenta Interagir do SketchUp faz: um armário abre a porta, uma
 * gaveta corre, um pé regulável sobe. O componente declara isso num atributo
 * `onclick`, num dialeto curto de comandos — `set("ABP",1,0)` alterna o
 * atributo ABP entre 1 e 0 a cada clique.
 *
 * O pacote de componentes dinâmicos avalia fórmulas, que são expressões; estes
 * são comandos, com efeito colateral. Por isso a leitura acontece aqui, do lado
 * de fora dele, e o resultado sai como valores a gravar — quem grava é o
 * chamador.
 */

/** Um atributo a mudar e o valor que ele passa a ter. */
export interface InteractionChange {
  /** Nó dono do atributo: vazio quer dizer o próprio nó que declarou o clique. */
  node: string;
  attribute: string;
  value: string | number;
}

export type InteractionDesiredState = "open" | "closed" | "toggle";
export type InteractionState = "open" | "closed" | "unknown";

export interface InteractionPlan {
  changes: InteractionChange[];
  stateBefore: InteractionState;
  stateAfter: InteractionState;
  alreadySatisfied: boolean;
}

type InteractionNode = {
  name: string;
  attributes: Record<string, { value: unknown; formula?: string }>;
};

/**
 * Escolhe a ação válida mais próxima da malha clicada.
 *
 * Alguns arquivos repetem `onclick` em todas as peças internas. A repetição
 * pode carregar uma ação de outro móvel: nas lixeiras, a frente herdava
 * `set("parent!Ab_Gav",...)`, mas aquele pai não declara `Ab_Gav`; a ação real
 * está um nível acima e altera `Ab`. Aceitar o primeiro texto encontrado cria
 * um atributo sem efeito e impede a subida até o manipulador certo.
 *
 * Um roteiro cujo alvo existe continua vencendo pela proximidade. Se o arquivo
 * usa legitimamente um atributo implícito, preservamos a compatibilidade e
 * devolvemos o manipulador mais próximo como último recurso.
 *
 * `onclick = PARENT!ONCLICK` não declara uma ação nova: apenas faz a ação do
 * conjunto aparecer também nos filhos. Executar o texto copiado no filho muda
 * o atributo sem qualificação (`AB`, por exemplo) na instância errada. Foi isso
 * que fazia só a frente das lixeiras sair, deixando cestos e ferragens dentro
 * do móvel. O clique deve subir até o nó que realmente declarou o roteiro.
 */
export function selectInteractionHolder<T extends InteractionNode>(
  chain: readonly T[],
  findNamed: (name: string) => T | null | undefined,
  changesAppearance?: (candidate: T) => boolean,
): T | undefined {
  const candidates = [...chain].reverse().filter(
    (node) => typeof node.attributes.onclick?.value === "string"
      && Boolean(node.attributes.onclick.value.trim())
      && !inheritsParentInteraction(node.attributes.onclick.formula),
  );

  for (const candidate of candidates) {
    const script = candidate.attributes.onclick!.value as string;
    const position = chain.indexOf(candidate);
    const parent = position > 0 ? chain[position - 1] : undefined;
    const byName = new Map(chain.map((node) => [node.name.toLocaleLowerCase("en-US"), node]));
    let targetCount = 0;
    let everyTargetExists = true;

    readInteraction(script, (nodeName, attribute) => {
      targetCount += 1;
      const target = !nodeName
        ? candidate
        : nodeName === "parent" || nodeName === "owner"
          ? parent
          : byName.get(nodeName) ?? findNamed(nodeName) ?? undefined;
      if (!target || target.attributes[attribute] === undefined) {
        everyTargetExists = false;
        return null;
      }
      const declared = target.attributes[attribute]?.value;
      return typeof declared === "string" || typeof declared === "number" ? declared : null;
    });

    if (targetCount > 0 && everyTargetExists
      && (!changesAppearance || changesAppearance(candidate))) return candidate;
  }
  // Sem uma avaliação visual, mantém-se a compatibilidade com arquivos que
  // criam atributos implícitos. Quando ela foi fornecida, retornar um roteiro
  // que comprovadamente não altera o desenho impediria a subida até a ação
  // correta e voltaria a produzir cliques sem efeito.
  return changesAppearance ? undefined : candidates[0];
}

/** A fórmula só encaminha o roteiro do pai; o dono da ação está acima. */
function inheritsParentInteraction(formula: string | undefined): boolean {
  return typeof formula === "string"
    && /^\s*(?:parent|owner)!onclick\s*$/i.test(formula);
}

/**
 * Se o clique da ferramenta Interagir pertence a algo que deve recebê-lo.
 *
 * Porta/janela e peça de componente ficam com o clique para executar sua ação.
 * O fundo e o piso não ficam: o evento precisa chegar à câmera para que o
 * arraste esquerdo continue orbitando também com a mão selecionada.
 */
export function shouldCaptureInteractionPointer(
  openingId: unknown,
  componentElementId: unknown,
  componentPath: unknown,
): boolean {
  return typeof openingId === "string"
    || (typeof componentElementId === "string" && typeof componentPath === "string");
}

/**
 * O que este clique muda, dado o roteiro e o que os atributos valem agora.
 *
 * Cada comando cicla pela lista de valores que declara: acha onde o valor atual
 * está e devolve o seguinte, voltando ao primeiro no fim. É assim que dois
 * cliques abrem e fecham a mesma porta.
 */
export function readInteraction(
  script: string,
  current: (node: string, attribute: string) => string | number | null,
): InteractionChange[] {
  return planInteraction(script, current, "toggle").changes;
}

/**
 * Planeja uma interação com estado desejado. Componentes importados costumam
 * representar o repouso por zero/off/false e a abertura por um valor diferente.
 * Quando esse significado não pode ser inferido, o plano fica `unknown` em vez
 * de adivinhar e inverter a peça.
 */
export function planInteraction(
  script: string,
  current: (node: string, attribute: string) => string | number | null,
  desiredState: InteractionDesiredState,
  resting?: (node: string, attribute: string) => string | number | null,
): InteractionPlan {
  const changes: InteractionChange[] = [];
  const beforeStates: InteractionState[] = [];
  const afterStates: InteractionState[] = [];
  let supportedCommands = 0;
  let satisfiedCommands = 0;
  for (const statement of splitStatements(script)) {
    const call = parseCall(statement);
    if (!call) continue;
    // `animate` e suas variações mudam o mesmo valor; o que muda é o caminho
    // até ele, que aqui é imediato.
    if (!["set", "animate", "animateslow", "animatecustom"].includes(call.name)) continue;
    const [target, ...values] = call.args;
    if (!target || !values.length) continue;

    const { node, attribute } = splitTarget(unquote(target));
    if (!attribute) continue;
    const parsed = values.map(readValue);
    const now = current(node, attribute);
    const declaredResting = resting?.(node, attribute) ?? null;
    const restingValue = parsed.some((value) => sameValue(value, declaredResting))
      ? declaredResting
      : null;
    const before = interactionStateForValue(now, restingValue);
    beforeStates.push(before);
    supportedCommands += 1;

    let next: string | number | undefined;
    if (desiredState === "toggle") {
      const index = parsed.findIndex((value) => sameValue(value, now));
      next = parsed[(index + 1) % parsed.length];
    } else {
      next = desiredState === "closed" && restingValue !== null
        ? parsed.find((value) => sameValue(value, restingValue))
        : desiredState === "open" && restingValue !== null
          ? parsed.find((value) => !sameValue(value, restingValue))
          : parsed.find((value) => interactionStateForValue(value, null) === desiredState);
      if (next !== undefined && sameValue(next, now)) satisfiedCommands += 1;
    }
    if (next === undefined) continue;
    const after = interactionStateForValue(next, restingValue);
    afterStates.push(after);
    if (desiredState === "toggle" || !sameValue(next, now)) changes.push({ node, attribute, value: next });
  }
  return {
    changes,
    stateBefore: combineInteractionStates(beforeStates),
    stateAfter: afterStates.length === supportedCommands ? combineInteractionStates(afterStates) : "unknown",
    alreadySatisfied: desiredState !== "toggle" && supportedCommands > 0 && satisfiedCommands === supportedCommands,
  };
}

function interactionStateForValue(
  value: string | number | null,
  restingValue: string | number | null = null,
): InteractionState {
  if (value === null) return "unknown";
  if (restingValue !== null) return sameValue(value, restingValue) ? "closed" : "open";
  if (typeof value === "number") return Math.abs(value) < 1e-6 ? "closed" : "open";
  const normalized = value.trim().toLocaleLowerCase("pt-BR");
  if (["0", "off", "false", "closed", "close", "fechado", "fechada"].includes(normalized)) return "closed";
  if (["1", "on", "true", "open", "opened", "aberto", "aberta"].includes(normalized)) return "open";
  const numeric = Number(normalized);
  if (Number.isFinite(numeric)) return Math.abs(numeric) < 1e-6 ? "closed" : "open";
  return "unknown";
}

function combineInteractionStates(states: InteractionState[]): InteractionState {
  if (!states.length || states.some((state) => state === "unknown")) return "unknown";
  return states.every((state) => state === "closed") ? "closed" : "open";
}

/** Divide em comandos, respeitando parênteses e aspas. */
function splitStatements(script: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < script.length; i += 1) {
    const char = script[i]!;
    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") quote = char;
    else if (char === "(") depth += 1;
    else if (char === ")") depth = Math.max(0, depth - 1);
    else if ((char === ";" || char === "\n") && depth === 0) {
      out.push(script.slice(start, i));
      start = i + 1;
    }
  }
  out.push(script.slice(start));
  return out.map((entry) => entry.trim()).filter(Boolean);
}

function parseCall(statement: string): { name: string; args: string[] } | null {
  const open = statement.indexOf("(");
  if (open <= 0 || !statement.trimEnd().endsWith(")")) return null;
  const name = statement.slice(0, open).trim().toLowerCase();
  const inside = statement.slice(open + 1, statement.lastIndexOf(")"));
  return { name, args: splitArgs(inside) };
}

function splitArgs(inside: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < inside.length; i += 1) {
    const char = inside[i]!;
    if (quote) {
      if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") quote = char;
    else if (char === "(") depth += 1;
    else if (char === ")") depth -= 1;
    else if (char === "," && depth === 0) {
      out.push(inside.slice(start, i));
      start = i + 1;
    }
  }
  out.push(inside.slice(start));
  return out.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

/** `Porta!ABP` aponta outro nó; `ABP` sozinho é o nó que declarou o clique. */
function splitTarget(target: string): { node: string; attribute: string } {
  const cut = target.lastIndexOf("!");
  if (cut < 0) return { node: "", attribute: target.trim().toLowerCase() };
  return {
    node: target.slice(0, cut).trim().toLowerCase(),
    attribute: target.slice(cut + 1).trim().toLowerCase(),
  };
}

function unquote(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && (trimmed[0] === '"' || trimmed[0] === "'") && trimmed.at(-1) === trimmed[0]) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function readValue(raw: string): string | number {
  const text = unquote(raw);
  const numeric = Number(text);
  return text !== "" && Number.isFinite(numeric) ? numeric : text;
}

/**
 * Comparação frouxa de propósito: o valor guardado pode ter voltado como texto,
 * e "1" e 1 são o mesmo estado de porta.
 */
function sameValue(candidate: string | number, now: string | number | null): boolean {
  if (now === null) return false;
  if (typeof candidate === "number" && typeof now === "number") {
    return Math.abs(candidate - now) < 1e-6;
  }
  return String(candidate).trim().toLowerCase() === String(now).trim().toLowerCase();
}
